import { useEffect, useState } from "react";
import { adminDeletionRequests, adminUpdateDeletionRequest } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "processing", "completed", "rejected"];

const STATUS_STYLE = {
    pending: "bl-badge bl-badge-medium",
    processing: "bl-badge bl-badge-neutral",
    completed: "bl-badge bl-badge-low",
    rejected: "bl-badge bl-badge-high",
};

function StatusBadge({ status }) {
    const s = status || "pending";
    return (
        <span className={STATUS_STYLE[s]} data-testid={`deletion-status-${s}`}>
            {s}
        </span>
    );
}

function Row({ req, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [status, setStatus] = useState(req.status || "pending");
    const [notes, setNotes] = useState(req.admin_notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        try {
            setSaving(true);
            await adminUpdateDeletionRequest(req.request_id, {
                status,
                admin_notes: notes,
            });
            toast.success("Deletion request updated");
            setEditing(false);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <tr
                className="border-b border-bl-soft hover:bg-bl-bg/40"
                data-testid={`deletion-row-${req.request_id}`}
            >
                <td className="px-5 py-3 text-bl text-sm">{req.email}</td>
                <td className="px-5 py-3 font-mono-bl text-xs text-bl-muted">
                    {req.user_id}
                </td>
                <td className="px-5 py-3">
                    <StatusBadge status={req.status} />
                </td>
                <td className="px-5 py-3 text-bl-muted text-xs whitespace-nowrap">
                    {req.requested_at
                        ? new Date(req.requested_at).toLocaleString()
                        : "—"}
                </td>
                <td className="px-5 py-3 text-bl-muted text-xs whitespace-nowrap">
                    {req.completed_at
                        ? new Date(req.completed_at).toLocaleString()
                        : "—"}
                </td>
                <td className="px-5 py-3">
                    <button
                        type="button"
                        onClick={() => setEditing((v) => !v)}
                        className="text-xs text-bl-primary hover:underline"
                        data-testid={`deletion-edit-btn-${req.request_id}`}
                    >
                        {editing ? "Close" : "Manage"}
                    </button>
                </td>
            </tr>
            {editing && (
                <tr className="border-b border-bl-soft bg-bl-bg/30">
                    <td colSpan={6} className="px-5 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <label className="text-xs text-bl-muted">
                                Status
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="mt-1 w-full px-2 py-2 bg-bl-surface rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid={`deletion-status-select-${req.request_id}`}
                                >
                                    {STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-xs text-bl-muted md:col-span-2">
                                Admin notes
                                <textarea
                                    rows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Internal handling notes…"
                                    className="mt-1 w-full px-2 py-2 bg-bl-surface rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid={`deletion-notes-input-${req.request_id}`}
                                />
                            </label>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={saving}
                                    className="bl-btn-primary w-full text-sm"
                                    data-testid={`deletion-save-btn-${req.request_id}`}
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function DeletionRequestsAdmin() {
    const [data, setData] = useState({ requests: [], counts: {} });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: "" });

    const load = async () => {
        try {
            setLoading(true);
            const d = await adminDeletionRequests(filter);
            setData(d);
        } catch {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.status]);

    const total = STATUSES.reduce((acc, s) => acc + (data.counts[s] || 0), 0);

    return (
        <div data-testid="deletion-requests-admin">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                    type="button"
                    onClick={() => setFilter({ status: "" })}
                    className={"bl-badge " + (filter.status === "" ? "bl-badge-neutral" : "")}
                    data-testid="deletion-filter-status-all"
                >
                    All · {total}
                </button>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setFilter({ status: s })}
                        className={
                            "bl-badge " +
                            (filter.status === s ? STATUS_STYLE[s] : "")
                        }
                        data-testid={`deletion-filter-status-${s}`}
                    >
                        {s} · {data.counts[s] || 0}
                    </button>
                ))}
            </div>
            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            {[
                                "Email",
                                "User ID",
                                "Status",
                                "Requested",
                                "Completed",
                                "",
                            ].map((h) => (
                                <th
                                    key={h}
                                    className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-5 py-8 text-center text-bl-muted">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && data.requests.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-5 py-8 text-center text-bl-muted">
                                    No deletion requests.
                                </td>
                            </tr>
                        )}
                        {!loading && data.requests.map((r) => (
                            <Row key={r.request_id} req={r} onChanged={load} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
